import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[transform,background-color,border-color,color,box-shadow,opacity] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.96] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:-mx-0.5 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[0_16px_32px_rgba(21,78,71,0.18)] hover:bg-primary/[0.92] hover:shadow-[0_22px_40px_rgba(21,78,71,0.24)]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-[0_14px_26px_rgba(220,38,38,0.16)] hover:bg-destructive/[0.92] hover:shadow-[0_18px_32px_rgba(220,38,38,0.2)]",
        outline:
          "border border-input/90 bg-background/80 shadow-[0_10px_22px_rgba(15,23,42,0.06)] hover:bg-accent/90 hover:text-accent-foreground hover:shadow-[0_14px_28px_rgba(15,23,42,0.1)]",
        secondary:
          "bg-secondary text-secondary-foreground shadow-[0_10px_22px_rgba(15,23,42,0.05)] hover:bg-secondary/90 hover:shadow-[0_14px_28px_rgba(15,23,42,0.08)]",
        ghost: "hover:bg-accent/70 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-11 rounded-md px-4 text-xs",
        lg: "h-11 rounded-md px-8 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
